import { Sidebar, Menu, MenuItem, SubMenu, useProSidebar } from 'react-pro-sidebar';
import TaskIcon from '@mui/icons-material/Task';
import WarningIcon from '@mui/icons-material/Warning';
import PaddingIcon from '@mui/icons-material/Padding';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Category, PriorityHigh } from '@mui/icons-material';
import SpeedIcon from '@mui/icons-material/Speed';
import ChecklistIcon from '@mui/icons-material/Checklist';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import SourceIcon from '@mui/icons-material/Source';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AuthUtil from './components/Utils/AuthUtil';

const sidebarColor = '#4D5061';
const sidebarHoverColor = '#65687c';
const iconColor = '#FFFFFF';
function CTSidebar (props) {
    const { collapseSidebar, collapsed } = useProSidebar();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isSuperAdmin = useMemo(() => AuthUtil.isSuperAdmin(userToken), [userToken]);
    const isAdmin = useMemo(() => AuthUtil.isAdmin(userToken), [userToken]);
    const isAuthorized = useMemo(() => AuthUtil.isAuthorized(userToken), [userToken]);

    const [isAdminMenuExpanded, setIsAdminMenuExpanded] = useState(false);

    useEffect(() => {
        collapseSidebar(props.navMenuCollapsed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.navMenuCollapsed]);

    const handleMenuItemClick = (menuItem) => {
        navigate(menuItem);
    };

    return (
        <div
            style={{
                position: 'fixed',
                // Header height is measured by Layout: the AppBar toolbar alone is 56px, but the
                // impersonation banner sits above it and changes height as its text wraps.
                top: 'var(--ct-header-h, 56px)',
                left: 0,
                bottom: 0,
                overflowY: 'auto',
                zIndex: 1100,
            }}
        >
            <Sidebar
                backgroundColor={sidebarColor}
                style={{ height: '100%', borderRight: 'none' }}
            >
                <Menu
                    menuItemStyles={{
                        button: {
                            backgroundColor: sidebarColor,
                            '&:hover': {
                                backgroundColor: sidebarHoverColor,
                            },
                        },
                    }}
                >
                    <MenuItem
                        icon={<TaskIcon sx={{ color: iconColor }} />}
                        onClick={() => handleMenuItemClick('/Tasks')}
                        style={{ color: iconColor }}
                    >
                        {t('sidebar.tasks')}
                    </MenuItem>
                    <MenuItem
                        icon={<WarningIcon sx={{ color: iconColor }} />}
                        onClick={() => handleMenuItemClick('/Hazards')}
                        style={{ color: iconColor }}
                    >
                        {t('sidebar.risks')}
                    </MenuItem>
                    {isAuthorized && (isSuperAdmin || isAdmin) &&
                        <MenuItem
                            icon={<PaddingIcon sx={{ color: iconColor }} />}
                            onClick={() => handleMenuItemClick('/Queries')}
                            style={{ color: iconColor }}
                        >
                            {t('queriesPage.title')}
                        </MenuItem>
                    }
                    <MenuItem
                        icon={<ChecklistIcon sx={{ color: iconColor }} />}
                        onClick={() => handleMenuItemClick('/AuditChecklists')}
                        style={{ color: iconColor }}
                    >
                        {t('auditChecklistsPage.title')}
                    </MenuItem>
                    <MenuItem
                        icon={<NewspaperIcon sx={{ color: iconColor }} />}
                        onClick={() => handleMenuItemClick('/resources')}
                        style={{ color: iconColor }}
                    >
                        {t('resourcesPage.title')}
                    </MenuItem>
                    {(isAdmin || isSuperAdmin) &&
                        <SubMenu label={t('sidebar.admin')} icon={<AdminPanelSettingsIcon/>} style={{ color: iconColor }} open={isAdminMenuExpanded} onClick={() => setIsAdminMenuExpanded(!isAdminMenuExpanded)}>
                            <MenuItem
                                active={isAdminMenuExpanded}
                                icon={<SupervisorAccountIcon sx={{ color: iconColor }}/>}
                                onClick={() => {
                                    handleMenuItemClick('/admin/Users');
                                    setIsAdminMenuExpanded(collapsed ? !isAdminMenuExpanded : isAdminMenuExpanded);
                                }}
                                style={{ color: iconColor }}
                            >
                                {t('usersPage.title')}
                            </MenuItem>
                            {isSuperAdmin &&
                                <MenuItem
                                    icon={<LocationCityIcon sx={{ color: iconColor }}/>}
                                    onClick={() => handleMenuItemClick('/admin/Organizations')}
                                    style={{ color: iconColor }}
                                >
                                    {t('organizationsPage.title')}
                                </MenuItem>
                            }
                            {isSuperAdmin &&
                                <MenuItem
                                    icon={<Category sx={{ color: iconColor }}/>}
                                    onClick={() => handleMenuItemClick('/admin/Categories')}
                                    style={{ color: iconColor }}
                                >
                                    {t('categoriesPage.title')}
                                </MenuItem>
                            }
                            {isSuperAdmin &&
                                <MenuItem
                                    icon={<PriorityHigh sx={{ color: iconColor }}/>}
                                    onClick={() => handleMenuItemClick('/admin/Priorities')}
                                    style={{ color: iconColor }}
                                >
                                    {t('prioritiesPage.title')}
                                </MenuItem>
                            }
                            {isSuperAdmin &&
                                <MenuItem
                                    icon={<SpeedIcon sx={{ color: iconColor }}/>}
                                    onClick={() => handleMenuItemClick('/admin/Statuses')}
                                    style={{ color: iconColor }}
                                >
                                    {t('statusesPage.title')}
                                </MenuItem>
                            }
                            {isSuperAdmin &&
                                <MenuItem
                                    icon={<LocationOnIcon sx={{ color: iconColor }}/>}
                                    onClick={() => handleMenuItemClick('/admin/Locations')}
                                    style={{ color: iconColor }}
                                >
                                    {t('locationsPage.title')}
                                </MenuItem>
                            }
                            { isSuperAdmin &&
                                <MenuItem
                                    icon={<ViewColumnIcon sx={{ color: iconColor }}/>}
                                    onClick={() => handleMenuItemClick('/admin/CustomColumns')}
                                    style={{ color: iconColor }}
                                >
                                    {t('customColumnsPage.title')}
                                </MenuItem>
                            }
                            { isSuperAdmin &&
                                <MenuItem
                                    icon={<ChecklistIcon sx={{ color: iconColor }}/>}
                                    onClick={() => handleMenuItemClick('/admin/AuditChecklistAdmin')}
                                    style={{ color: iconColor }}
                                >
                                    {t('auditChecklistAdminPage.title')}
                                </MenuItem>
                            }
                            {isSuperAdmin &&
                                <MenuItem
                                    icon={<SourceIcon sx={{ color: iconColor }}/>}
                                    onClick={() => handleMenuItemClick('/admin/RiskSource')}
                                    style={{ color: iconColor }}
                                >
                                    {t('riskSourcesPage.title')}
                                </MenuItem>
                            }
                        </SubMenu>
                    }
                </Menu>
            </Sidebar>
        </div>
    );
}

CTSidebar.propTypes = {
    navMenuCollapsed: PropTypes.bool.isRequired,
};

export default CTSidebar;
